import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import queryString from 'query-string';
// type
import api from '@/api';
import { IAccountRoleTypeOptions } from '@/models/account/types';
// styles
import styles from './styles.module.scss';
// images
import Smile from '@/components/ui/new-icons/Smile';
import { ForwardArrow } from '@/components/landing-page-templates/ConfigLandingPage/LandingPageIcons';
import { useRedirectAfterLogin } from '@/hooks/use-redirect-after-login';
import { zoneTypes } from '@/models/event/types';
import { useMemoizedSelector } from '@/hooks/use-memoized-selector';
import {
  makeSelectStagesByEventId,
  makeSelectStudioIdByStageId,
} from '@/models/event';
import { EEventType } from '@/types/event';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import { getStudioAppBasePath } from '@/utils/zuddlUrls';

const EnterEventConfirmationSection = ({
  buttonStyle,
  currentUser,
  currentUserEventPermissions,
  event,
  isEmbedView = false,
  isLoginFlowOnly = false,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = queryString.parse(search);
  const { refId, ref = '', embedMagicTokenVerified = 'false' } = searchParams;
  const { redirectPageAfterCheckIn } = useRedirectAfterLogin({ currentUser });

  const stages = useMemoizedSelector(makeSelectStagesByEventId, event.eventId);
  const isWebinarEvent = event.type === EEventType.WEBINAR;
  const isSeriesParent = event.type === EEventType.SERIES;
  const stageId = stages[0]?.stageId;

  useEffect(() => {
    if (isWebinarEvent) {
      dispatch({
        type: 'event/getStudioIdForStageId',
        payload: { stageId },
      });
    }
  }, [stageId, isWebinarEvent]);

  const stageStudioId = useMemoizedSelector(
    makeSelectStudioIdByStageId,
    stageId,
  );

  const redirectPage = (redirectUrl: string) => {
    if (isEmbedView || isLoginFlowOnly) {
      // sendig message to parent window
      let myParent = window.top;
      if (window.parent !== window.top) {
        myParent = window.parent;
      }
      myParent?.postMessage(
        {
          action: 'ENTER_NOW_CLICK',
          redirectUrl: `${window.location.origin}${redirectUrl}`,
        },
        '*',
      );
      return;
    }
    history.push(redirectUrl);
  };

  const handleCheckIn = () => {
    const { role } = currentUserEventPermissions;
    const { eventId } = event;

    if (ref) {
      redirectPage(ref);
    }
    // NOTE - this was previously using the account's roles field; it should be using the event role's specific role
    //  e.g. a speaker at one event should not be considered a speaker for all events.
    else if (currentUserEventPermissions.role) {
      // TODO - the following API call is made again in the speaker redirect flow; move it before the if statement above (DRY)
      api.event.getZoneSetUp(eventId).then(({ data: zoneSetupList }) => {
        const zoneSetup = {};
        zoneSetupList.forEach(item => {
          if (item.type === zoneTypes.BOOTH) {
            Object.assign(zoneSetup, {
              [item.zoneId]: item.active && item.isVisible,
            });
          } else {
            Object.assign(zoneSetup, {
              [item.type]: item.active && item.isVisible,
            });
          }
        });
        if (role === IAccountRoleTypeOptions.ATTENDEE) {
          // TODO - this is may an unnecessary API call; eventRole and currentUserEventPermissions are the same object while testing
          redirectPage(redirectPageAfterCheckIn(role, zoneSetup));
        } else if (
          role === IAccountRoleTypeOptions.SPEAKER ||
          role === IAccountRoleTypeOptions.ORGANIZER ||
          role === IAccountRoleTypeOptions.MODERATOR
        ) {
          if (
            isWebinarEvent &&
            role === IAccountRoleTypeOptions.SPEAKER &&
            !!stageStudioId
          ) {
            window.open(
              `${getStudioAppBasePath()}/webinar/${stageStudioId}`,
              '_self',
            );
          } else {
            redirectPage(redirectPageAfterCheckIn(role, zoneSetup));
          }
        } else if (zoneSetupList && zoneSetupList.length > 0) {
          redirectPage(
            redirectPageAfterCheckIn(
              IAccountRoleTypeOptions.ATTENDEE,
              zoneSetup,
            ),
          );
        } else {
          // Handle default case of event with no zone configuration
          // TODO - is this even a case which should happen (event with no zone config)? If not, add warning log.
          redirectPage(`/l/event/${eventId}/stages`);
        }
      });
    } else {
      // TODO - failed to get role from the current user? This shouldn't happen, add log warning.
      api.event.getZoneSetUp(eventId).then(({ data: zoneSetupList }) => {
        const zoneSetup = {};
        zoneSetupList.forEach(item => {
          if (item.type === zoneTypes.BOOTH) {
            Object.assign(zoneSetup, {
              [item.zoneId]: item.active && item.isVisible,
            });
          } else {
            Object.assign(zoneSetup, {
              [item.type]: item.active && item.isVisible,
            });
          }
        });
        // NOTE - this is a failed case (couldn't get the role from event role)
        //  so defaulting to handling it for an speaker.
        // TODO - Should the default case be for attendee flow as opposed to speaker flow?
        redirectPage(
          redirectPageAfterCheckIn(IAccountRoleTypeOptions.SPEAKER, zoneSetup),
        );
      });
    }
  };

  useEffect(() => {
    if (isEmbedView && currentUser && embedMagicTokenVerified === 'true') {
      // enter now button action automatically triggered if page loading from duda
      handleCheckIn();
    }
  }, [isEmbedView, currentUser, embedMagicTokenVerified]);

  useEffect(() => {
    //We have to check this condition to identify the event is series child
    //event?.seriesParentEventId is present in all the series events including parent event
    //and series parent is identified by type "SERIES" in event
    if (event?.seriesParentEventId && !isSeriesParent) {
      handleCheckIn();
    }
  }, [isSeriesParent, event]);

  // Check if name is blank
  const showName =
    (currentUser &&
      currentUser.firstName === '' &&
      currentUser.lastName === '') ||
    (currentUser.firstName === 'Event' &&
      currentUser.lastName === 'Attendee') ||
    currentUser.firstName === currentUser.email;

  // Check if name is blank
  const name = showName ? 'Attendee' : currentUser.firstName;

  return (
    <div className={styles.mainContainer}>
      <div className={styles.mainHeading}>Hi {name}</div>
      <div className={styles.heading}>You&apos;re right on time.</div>
      <div className={styles.button}>
        <button
          type="submit"
          onSubmit={handleCheckIn}
          onClick={handleCheckIn}
          style={buttonStyle}
          className={classnames(styles.submitButton, {
            [styles.embedSubmitButton]: isEmbedView,
          })}
        >
          {/* Forward Arrow SVG */}
          <ForwardArrow color={buttonStyle.color} />
          <p className={styles.buttonText}>Enter Now</p>
        </button>
      </div>
    </div>
  );
};

export default EnterEventConfirmationSection;
